import apiEndPoints from '../../../routeConfig';
import { ApiService } from '../../../../service/api.service';
import { attempt, resultMessage } from '../../../../service/apiResult';

/**
 * Bulk eK Score re-scoring.
 *
 * Re-scores every farmer who has answered the questionnaire, from their own last answers, so a change
 * to what the engine reads (weather data switched on, a new catalogue) reaches existing farmers too.
 * Each re-score calls NASA POWER and data.gov.in, so a run goes one farmer at a time and, by default,
 * only in the off-peak window. Scores and confidence shift for the farmers it reaches, which SRFS and
 * the banks see, so they should be told before a run is started.
 */

const apiService = new ApiService();

const body = (result) => result?.data?.data ?? result?.data ?? null;
const ok = (data) => ({ success: true, data });
const fail = (message) => ({ success: false, error: { message } });

export const DEFAULT_PACE_MS = 2000;
export const MIN_PACE_MS = 500;
export const MAX_PACE_MS = 60000;

export const ACTIVE_STATUSES = ['RUNNING', 'PAUSED'];

export function isActive(run) {
  return Boolean(run) && ACTIVE_STATUSES.includes(run.status);
}

/** How far a run has got, 0-100: farmers dealt with (re-scored, skipped or failed) out of the total. */
export function progressPercent(run) {
  if (!run || !run.total) return run?.status === 'COMPLETED' ? 100 : 0;
  const handled = (run.done || 0) + (run.skipped || 0) + (run.failed || 0);
  return Math.min(100, Math.round((handled / run.total) * 100));
}

export function validatePace(seconds) {
  const ms = Math.round(Number(seconds) * 1000);
  if (seconds === '' || !Number.isFinite(ms)) return 'Enter the seconds between farmers.';
  if (ms < MIN_PACE_MS || ms > MAX_PACE_MS) return 'Use between 0.5 and 60 seconds.';
  return null;
}

/** "about 41 min", "about 3 h 20 min". */
export function formatDuration(minutes) {
  const total = Math.max(0, Math.round(Number(minutes) || 0));
  if (total < 60) return `about ${total} min`;
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  return rest ? `about ${hours} h ${rest} min` : `about ${hours} h`;
}

/* ---------------------------------------------------------------- storage */

export async function getPreview(paceMs) {
  const query = paceMs ? `?paceMs=${paceMs}` : '';
  const result = await attempt(() => apiService.get(`${apiEndPoints.rescoreRuns}/preview${query}`));
  if (!result.success) return fail(resultMessage(result, 'Could not work out how many farmers would be re-scored.'));
  return ok(body(result));
}

export async function getRuns() {
  const result = await attempt(() => apiService.get(apiEndPoints.rescoreRuns));
  if (!result.success) return fail(resultMessage(result, 'Could not load the re-score runs.'));
  return ok(body(result) || []);
}

export async function startRun({ paceMs, offPeakOnly }) {
  const result = await attempt(() => apiService.post(apiEndPoints.rescoreRuns, { paceMs, offPeakOnly }));
  if (!result.success) return fail(resultMessage(result, 'Could not start the run.'));
  return ok(body(result));
}

async function control(id, action, message) {
  const result = await attempt(() => apiService.post(`${apiEndPoints.rescoreRuns}/${id}/${action}`, {}));
  if (!result.success) return fail(resultMessage(result, message));
  return ok(body(result));
}

export const pauseRun = (id) => control(id, 'pause', 'Could not pause the run.');
export const resumeRun = (id) => control(id, 'resume', 'Could not resume the run.');
export const cancelRun = (id) => control(id, 'cancel', 'Could not cancel the run.');
